// ─── components/Shared/GateNotice.jsx ────────────────────────────────────────
// The way forward when the server refuses an action until something is set
// up (verify email, connect payouts, finish a profile). Those refusals carry a
// `code`, and most screens only showed their message: "Set up payouts before
// publishing" with nowhere to click. AuthContext's fetch wrapper announces
// any of the codes below (`uf:gate`), and this offers the fix in one click,
// sending people back to the page they were on wherever the fix allows it.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PiArrowRight, PiX } from 'react-icons/pi';

export const GATE_CODES = [
  'EMAIL_NOT_VERIFIED',
  'STRIPE_NOT_CONNECTED',
  'SELLER_ONBOARDING_INCOMPLETE',
  'BUSINESS_PROFILE_INCOMPLETE',
  'PROFILE_INCOMPLETE',
  'UNIVERSITY_NOT_VERIFIED',
];

const FIX = {
  EMAIL_NOT_VERIFIED:           { title: 'Verify your email first', label: 'Resend the link' },
  STRIPE_NOT_CONNECTED:         { title: 'Set up payouts first', label: 'Set up payouts' },
  SELLER_ONBOARDING_INCOMPLETE: { title: 'Finish your seller profile', label: 'Finish it' },
  BUSINESS_PROFILE_INCOMPLETE:  { title: 'Finish your business profile', label: 'Finish it' },
  PROFILE_INCOMPLETE:           { title: 'Finish your profile', label: 'Finish it' },
  UNIVERSITY_NOT_VERIFIED:      { title: 'Verify your university', label: 'Verify it' },
};

export default function GateNotice() {
  const navigate = useNavigate();
  const location = useLocation();
  const [gate, setGate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const here = location.pathname + location.search;
  const hereRef = useRef(here);
  hereRef.current = here;

  useEffect(() => {
    const onGate = (e) => {
      if (!FIX[e.detail?.code]) return;
      setGate({ ...e.detail, from: hereRef.current });
      setDone('');
      setBusy(false);
    };
    window.addEventListener('uf:gate', onGate);
    return () => window.removeEventListener('uf:gate', onGate);
  }, []);

  // Leaving the page it was raised on means it's no longer about this screen.
  useEffect(() => {
    if (gate && gate.from !== here) setGate(null);
  }, [here, gate]);

  if (!gate) return null;
  const fix = FIX[gate.code];
  const back = gate.from;

  async function run() {
    switch (gate.code) {
      case 'EMAIL_NOT_VERIFIED': {
        setBusy(true);
        const res = await fetch('/api/users/resend-verification', { method: 'POST', credentials: 'include' }).catch(() => null);
        setBusy(false);
        setDone(res?.ok ? 'Sent. Open the link in that email, then try again here.' : 'Couldn’t send it. Try again in a minute.');
        return;
      }
      case 'STRIPE_NOT_CONNECTED': {
        setBusy(true);
        const res = await fetch('/api/stripe/connect/onboard', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ returnPath: back }),
        }).catch(() => null);
        const data = res ? await res.json().catch(() => ({})) : {};
        if (res?.ok && data.url) { window.location.href = data.url; return; }
        setBusy(false);
        setDone(data.message || 'Couldn’t open Stripe. Try again from Settings, Getting paid.');
        return;
      }
      case 'SELLER_ONBOARDING_INCOMPLETE':
        navigate('/seller-onboarding', { state: { next: back } });
        break;
      case 'BUSINESS_PROFILE_INCOMPLETE':
        navigate('/client-onboarding', { state: { next: back } });
        break;
      // Refused on applying to a job (headline, bio, skills). Seller onboarding
      // asks for exactly those and honours `next`, the same route the job
      // page's own "Finish my profile" takes, so it lands back on the job.
      case 'PROFILE_INCOMPLETE':
        navigate('/seller-onboarding', { state: { reason: 'apply-to-job', next: back } });
        break;
      case 'UNIVERSITY_NOT_VERIFIED':
        navigate(`/settings?section=university-verification&returnTo=${encodeURIComponent(back)}`);
        break;
      default:
    }
    setGate(null);
  }

  return (
    <div
      role="alert"
      className="fixed inset-x-4 bottom-4 z-[130] mx-auto max-w-[440px] rounded-[14px] border border-press-border bg-press-card p-4 font-academy shadow-[0_12px_32px_rgba(18,22,28,0.16)] sm:left-auto sm:right-6 sm:mx-0"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[15px] font-bold text-press-ink">{fix.title}</p>
          <p className="m-0 mt-1 text-[13px] leading-snug text-press-muted">{done || gate.message}</p>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setGate(null)}
          className="grid h-8 w-8 flex-none place-items-center rounded-full text-press-muted transition hover:bg-press-paper-2 hover:text-press-ink"
        >
          <PiX size={15} aria-hidden="true" />
        </button>
      </div>
      {!done && (
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="mt-3 inline-flex min-h-10 items-center gap-1.5 rounded-full bg-press-ink px-4 text-[13px] font-bold text-press-paper transition hover:bg-press-ink/90 disabled:opacity-50"
        >
          {busy ? 'One moment…' : fix.label} <PiArrowRight size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
